import { AppTextField } from 'src/feature/form/component/analog/AppTextField'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import type { FocusEvent, ChangeEvent } from 'react'
import { useCallback, useEffect, useState } from 'react'
import type { SxProps, Theme } from '@mui/material/styles'
import { Word } from 'src/feature/localization/component/atom/Word'
import { observer } from 'mobx-react-lite'
import { ColorTokens } from 'src/feature/theme/model/Palette'

const DEFAULT_COUNT = '0'
const DEFAULT_DEBOUNCE_DELAY = 1500
const SEARCH_FIELD_SX = { mb: 0 }

export type SkillsSearchBarProps = {
  count?: string | number
  value?: string
  onDebouncedChange?: (searchText: string) => void
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  debounceDelay?: number
  sx?: SxProps<Theme>
}

export const SkillsSearchBar = observer(({
  count = DEFAULT_COUNT,
  value,
  onDebouncedChange,
  onFocus,
  onBlur,
  debounceDelay = DEFAULT_DEBOUNCE_DELAY,
  sx,
}: SkillsSearchBarProps) => {
  const { translation } = useLocalizationStore()
  const texts = translation.pages.eLearning

  const [inputValue, setInputValue] = useState(value ?? '')

  useEffect(() => {
    setInputValue(value ?? '')
  }, [value])

  useEffect(() => {
    const timer = setTimeout(() => {
      onDebouncedChange?.(inputValue)
    }, debounceDelay)

    return () => clearTimeout(timer)
  }, [inputValue, onDebouncedChange, debounceDelay])

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }, [])

  return (
    <AppTextField
      placeholder={texts.search()}
      value={inputValue}
      sx={SEARCH_FIELD_SX}
      InputProps={{
        sx: { ...styles.field, ...sx },
        endAdornment: (
          <Word sx={styles.pill}>{count ?? DEFAULT_COUNT}</Word>
        ),
      }}
      onChange={handleInputChange}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  )
})

const styles = {
  field: {
    border: 0,
    backgroundColor: ColorTokens.primary10,
    input: {
      fontSize: '12px',
    },
    '&:hover': {
      border: 0,
    },
    '&.Mui-focused': {
      border: 0,
    },
    '&.Mui-focused:hover': {
      border: 0,
    },
    '&:active': {
      border: 0,
    },
  },
  pill: {
    borderRadius: '8px',
    border: 0,
    backgroundColor: ColorTokens.primary10,
    padding: '0 10px',
    color: 'text.primary',
    textAlign: 'center',
  },
}